import { useCallback, useMemo, useState } from 'react';
import { useField } from 'react-final-form';

import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';

import {
  extractPrescriptionOptions,
  getSelectedPrescriptionKeys,
  LINK_PRESCRIPTIONS_FIELD,
  TPrescriptionRow,
} from '../functions/prescriptions';

const NO_ROWS: TPrescriptionRow[] = [];

/** Одна пустая строка выбора — показываем, когда подходящих предписаний не нашлось */
const EMPTY_ROWS: TPrescriptionRow[] = [undefined];

const getKeysHash = (keys: string[]) => keys.slice().sort().join(',');

/**
 * Состояние поля "Предписание" для ссылки на паспорт рейдового осмотра.
 */
export const useLinkPrescriptions = (formRef?: IKnmFormRef) => {
  const [options, setOptions] = useState<ISelectOption[]>([]);
  /** Предписания, которые уже были сохранены в паспорт; undefined - сохранение ещё не выполнялось */
  const [appliedKeys, setAppliedKeys] = useState<string[] | undefined>();

  const { input } = useField<TPrescriptionRow[]>(LINK_PRESCRIPTIONS_FIELD, { subscription: { value: true } });
  const rows = Array.isArray(input.value) ? input.value : NO_ROWS;

  const changeRows = useCallback(
    (nextRows?: TPrescriptionRow[]) => {
      formRef?.current?.change(LINK_PRESCRIPTIONS_FIELD, nextRows);
    },
    [formRef],
  );

  const reset = useCallback(() => {
    setOptions([]);
    setAppliedKeys(undefined);
    changeRows(undefined);
  }, [changeRows]);

  const fill = useCallback(
    (linkedKnm: IKnm, isEnablePrescriptionCheck: boolean, erknmStartDateString?: string) => {
      const nextOptions = extractPrescriptionOptions(linkedKnm, isEnablePrescriptionCheck, erknmStartDateString);

      setOptions(nextOptions);
      setAppliedKeys(undefined);
      // По умолчанию подставляем все подходящие предписания, их можно изменить или удалить
      changeRows(nextOptions.length ? nextOptions : EMPTY_ROWS);
    },
    [changeRows],
  );

  const selectedPrescriptionKeys = useMemo(() => getSelectedPrescriptionKeys(rows), [rows]);

  /** Отметить текущий набор предписаний как применённый к паспорту (нажата кнопка "Сохранить") */
  const markApplied = useCallback(() => {
    setAppliedKeys(selectedPrescriptionKeys);
  }, [selectedPrescriptionKeys]);

  return {
    options,
    selectedPrescriptionKeys,
    // Предписание обязательно в каждой строке, поэтому проверяем не наличие выбранных, а отсутствие пустых
    isFilled: rows.length > 0 && rows.every(Boolean),
    // Единственное подходящее предписание подставлено автоматически - менять нельзя
    isEditable: options.length !== 1,
    // Паспорт уже заполнен по выбранным предписаниям
    isApplied: appliedKeys !== undefined,
    // Предписания изменили после последнего сохранения
    isChanged: appliedKeys !== undefined && getKeysHash(appliedKeys) !== getKeysHash(selectedPrescriptionKeys),
    reset,
    fill,
    markApplied,
  };
};
